"use client";

import { CheckmarkCircle02Icon, Loading03Icon, Notification01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { formatDistanceToNow } from "date-fns";
import type { Route } from "next";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { getUnreadNotificationCount, getUnreadNotifications, markAllNotificationsAsRead, markNotificationAsRead } from "@/app/notifications/_actions";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { authClient } from "@/lib/auth-client";
import { cn } from "@/lib/utils";
const POLL_INTERVAL_MS = 30_000;
const NOTIFICATION_SKELETON_KEYS = ["notification-skeleton-1", "notification-skeleton-2", "notification-skeleton-3"] as const;
type Notification = {
  id: string;
  title: string;
  body: string;
  link: string | null;
  isRead: boolean;
  type: string;
  createdAt: Date | string; // Handle both Date objects and ISO strings
  data: unknown;
};
const asRoute = (path: string) => path as Route;
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia } from "@/components/ui/empty";
export function NotificationCenter() {
  const router = useRouter();
  const {
    data: session
  } = authClient.useSession();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [markingAsRead, setMarkingAsRead] = useState<string | null>(null);
  const [markingAll, setMarkingAll] = useState(false);
  const refreshCount = useCallback(async () => {
    if (!session?.user?.id) return;
    try {
      const count = await getUnreadNotificationCount();
      setUnreadCount(count);
    } catch {
      // silent — polling failure shouldn't disrupt the UI
    }
  }, [session?.user?.id]);
  const fetchNotifications = useCallback(async () => {
    if (!session?.user?.id) return;
    setIsLoading(true);
    try {
      const [data, count_0] = await Promise.all([getUnreadNotifications(5), getUnreadNotificationCount()]);

      // Safe cast assuming your server action returns compatible data
      setNotifications(data as Notification[]);
      setUnreadCount(count_0);
    } catch (error) {
      console.error("Failed to fetch notifications", error);
    } finally {
      setIsLoading(false);
    }
  }, [session?.user?.id]);

  // Fetch on popover open/close
  useEffect(() => {
    if (open) {
      fetchNotifications();
    } else if (session?.user?.id) {
      refreshCount();
    }
  }, [open, session?.user?.id, fetchNotifications, refreshCount]);

  // Poll unread count every 30s so the badge stays current
  useEffect(() => {
    if (!session?.user?.id) return;
    refreshCount();
    const interval = setInterval(refreshCount, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [session?.user?.id, refreshCount]);

  // Instantly refresh when a foreground FCM message arrives
  useEffect(() => {
    const handler = () => {
      refreshCount();
    };
    window.addEventListener("navi:notification", handler);
    return () => window.removeEventListener("navi:notification", handler);
  }, [refreshCount]);
  const handleMarkAsRead = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!session?.user?.id) return;
    setMarkingAsRead(id);
    try {
      await markNotificationAsRead(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      setUnreadCount(prev_0 => Math.max(0, prev_0 - 1));
    } catch {
      toast.error("Failed to mark as read");
    } finally {
      setMarkingAsRead(null);
    }
  };
  const handleNotificationClick = (id_0: string, link: string | null) => {
    if (!session?.user?.id) return;

    // 1. Immediately route to the link if it exists to keep UI responsive
    if (link) {
      setOpen(false);
      router.push(asRoute(link));
    }

    // 2. Optimistically update the UI and mark as read in the background
    const notification = notifications.find(n_0 => n_0.id === id_0);
    if (notification && !notification.isRead) {
      setNotifications(prev_1 => prev_1.filter(n_1 => n_1.id !== id_0));
      setUnreadCount(prev_2 => Math.max(0, prev_2 - 1));
      markNotificationAsRead(id_0).catch(() => {
        toast.error("Failed to process notification");
      });
    }
  };
  const handleMarkAllAsRead = async () => {
    if (!session?.user?.id) return;
    if (markingAll) return;
    setMarkingAll(true);
    try {
      await markAllNotificationsAsRead();
      setNotifications([]);
      setUnreadCount(0);
      toast.success("All marked as read");
    } catch {
      toast.error("Failed to mark all as read");
    } finally {
      setMarkingAll(false);
    }
  };
  if (!session) return null;
  return <Popover open={open} onOpenChange={setOpen}>
			<Tooltip open={open ? false : undefined}>
				<TooltipTrigger asChild>
					<PopoverTrigger className={buttonVariants({
          variant: "ghost",
          size: "icon",
          className: "relative flex items-center justify-center p-5"
        })}>
						<HugeiconsIcon icon={Notification01Icon} className="size-6" />
						{unreadCount > 0 && <Badge className="absolute -top-1 -right-1 flex size-5 items-center justify-center rounded-full bg-foreground p-0 font-outfit text-[10px] text-background">
								{unreadCount > 9 ? "9+" : unreadCount}
							</Badge>}
					</PopoverTrigger>
				</TooltipTrigger>
				<TooltipContent side="bottom">Notifications</TooltipContent>
			</Tooltip>

			<PopoverContent className="w-[min(25rem,calc(100vw-1rem))] shrink-0 gap-0 p-0 font-jakarta" align="end" collisionPadding={8}>
				<div className="flex items-center justify-between p-4">
					<h4 className="font-semibold leading-none">Notifications</h4>
					{unreadCount > 0 && <Button variant="ghost" size="sm" className="h-auto px-2 text-muted-foreground text-xs hover:text-foreground" onClick={handleMarkAllAsRead} disabled={markingAll}>
							{markingAll ? <HugeiconsIcon icon={Loading03Icon} className="mr-1 h-3 w-3 animate-spin" /> : <HugeiconsIcon icon={CheckmarkCircle02Icon} className="mr-1 h-3 w-3" />}
							{markingAll ? "Marking..." : "Mark all read"}
						</Button>}
				</div>

				<Separator className="my-0" />

				{/* This selector targets the inner Radix viewport div directly.
           This is the most robust way to handle overflow in popovers.
        */}
				<ScrollArea className="w-full [&>[data-radix-scroll-area-viewport]]:max-h-[300px]">
					{isLoading ? <div className="space-y-4 p-4">
							{NOTIFICATION_SKELETON_KEYS.map(key => <div key={key} className="flex gap-3">
									<Skeleton className="h-10 w-10 shrink-0 rounded-full" />
									<div className="flex-1 space-y-2">
										<Skeleton className="h-4 w-full" />
										<Skeleton className="h-3 w-2/3" />
									</div>
								</div>)}
						</div> : notifications.length === 0 ? <Empty className="border-none p-6">
							<EmptyHeader>
								<EmptyMedia variant="icon">
									<HugeiconsIcon icon={Notification01Icon} className="opacity-50" />
								</EmptyMedia>
								<EmptyDescription>No new notifications</EmptyDescription>
							</EmptyHeader>
						</Empty> :
        // Removed 'flex' here to let block layout handle height naturally
        <div className="flex flex-col">
							{notifications.map(notification_0 => <div key={notification_0.id} className={cn("group flex cursor-pointer items-start gap-2 border-b p-4 text-left text-sm transition-colors last:border-0", "hover:bg-muted/40 dark:hover:bg-muted/60", !notification_0.isRead && "border-l-2 border-l-primary pl-[calc(1rem-2px)] dark:bg-muted/40")}>
									<button type="button" className="flex min-w-0 flex-1 flex-col items-start gap-1 text-left" onClick={() => handleNotificationClick(notification_0.id, notification_0.link)}>
										<div className="flex w-full justify-between gap-2">
											<span className={`line-clamp-1 font-medium ${!notification_0.isRead ? "text-primary" : ""}`}>
												{notification_0.title}
											</span>
											<span className="shrink-0 text-[10px] text-muted-foreground">
												{formatDistanceToNow(new Date(notification_0.createdAt), {
                    addSuffix: true
                  })}
											</span>
										</div>
										<p className="line-clamp-2 w-full text-muted-foreground text-xs">
											{notification_0.body}
										</p>
									</button>
									{!notification_0.isRead && <Button variant="ghost" size="icon" className="z-10 ml-1 h-4 w-4 shrink-0 text-muted-foreground hover:text-primary" onClick={e_0 => handleMarkAsRead(e_0, notification_0.id)} title="Mark as read" disabled={markingAsRead === notification_0.id}>
											{markingAsRead === notification_0.id ? <HugeiconsIcon icon={Loading03Icon} className="h-3 w-3 animate-spin" /> : <HugeiconsIcon icon={CheckmarkCircle02Icon} className="h-3 w-3" />}
										</Button>}
								</div>)}
						</div>}
				</ScrollArea>

				<Separator />

				<div className="p-2">
					<Link href="/notifications" passHref onClick={() => setOpen(false)}>
						<Button variant="outline" className="h-8 w-full text-xs">
							View all notifications
						</Button>
					</Link>
				</div>
			</PopoverContent>
		</Popover>;
}